import { InvalidOperationError } from "@baseline/common/errors";
import { Context } from "./context";
import { Handler } from "./handler";
import { PredicateFunction, Route, RoutePredicate, RouteSpecs } from "./route";
import { RoutePath, RoutePathArgumentTypes, RoutePathArgumentsFor } from "./route-path";
import { Router } from "./router";


export interface RouterBuilder<C extends Context = Context>
{
    build(): Router;
    when<T extends Omit<RouteOptions, "path">>(
        path: string,
        options?: T
    ): RouteBuilder<ContextFor<T> & C>;
    when<T extends RoutePathArgumentTypes = {}>(
        routePath: RoutePath<RoutePathArgumentsFor<T>>,
        options?: Omit<RouteOptions, "path" | "args">
    ): RouteBuilder<Context<RoutePathArgumentsFor<T>> & C>;
    when<T extends Omit<RouteOptions, "test">>(
        predicate: PredicateFunction,
        options?: T
    ): RouteBuilder<ContextFor<T> & C>;
    when<T extends RouteOptions>(
        options: T
    ): RouteBuilder<ContextFor<T> & C>;
}


export namespace RouterBuilder
{
    export function create<C extends Context = Context>(): RouterBuilder<C>;
    export function create(): RouterBuilder
    {
        return new DefaultRouterBuilder();
    }
}


export interface RouteBuilder<C extends Context = Context>
{
    handle(...handlers: Handler<C>[]): RouterBuilder<C>;
    route(routing: (builder: RouterBuilder<C>) => void): RouterBuilder<C>;
}


export type ContextFor<R extends RoutePredicate> = Context<RoutePathArgumentsFor<R["args"]>>;


export class DefaultRouterBuilder implements RouterBuilder, RouteBuilder
{
    constructor()
    {
    }

    when(...args: any[]): this
    {
        if (this.predicate)
            throw new InvalidOperationError(`Multiple predicates supplied for route.`);

        const [path, test, options]: [path: string | RoutePath, test: PredicateFunction, options: RoutePredicate] =
            (args[0] instanceof RoutePath || typeof args[0] == "string")
                ? [args[0], null, args[1]]
                : (typeof args[0] == "function")
                    ? [null, args[0], args[1]]
                    : [null, null, args[0]];

        this.predicate =
        {
            ...options,
            ...(path && { path }),
            ...(test && { test }),
        };

        return this;
    }

    handle(...handlers: Handler[]): this
    {
        if (!this.predicate)
            throw new InvalidOperationError(`No predicate supplied for route.`);

        const predicate = this.predicate;
        this.predicate = null;

        predicate.endPoint ??= false;

        this.routes.push({ predicate, handlers });

        return this;
    }

    route(routing: (builder: RouterBuilder) => void): this
    {
        if (!this.predicate)
            throw new InvalidOperationError(`No predicate supplied for route.`);

        const builder = this.createRouterBuilder();
        routing(builder);
        const router = builder.build();
        const handler = router.route;

        const predicate = this.predicate;
        this.predicate = null;

        predicate.endPoint ??= false;

        this.routes.push({ predicate, handlers: [handler] });

        return this;
    }

    build(): Router
    {
        const routes: Route[] = this.routes.map(Route.build);
        return new Router(routes);
    }


    protected createRouterBuilder(): RouterBuilder
    {
        return new DefaultRouterBuilder();
    }


    protected predicate: RoutePredicate;
    protected routes: RouteSpecs[] = [];
}


export type RouteOptions = RoutePredicate;
